import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { AlertTriangle, ArrowRight, ShieldCheck } from 'lucide-react';
import { formatCurrency } from '../lib/utils';

export default function PreTransferConfirm({ open, onClose, onConfirm, details, loading }) {
  if (!details) return null;

  const {
    type = 'Transfer',
    amount = 0,
    recipient = '',
    fromAccount = '',
    convertedAmount,
    fxRate,
    targetCurrency,
    extraFields = [],
  } = details;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex flex-col items-center text-center pt-2">
            <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mb-3">
              <ShieldCheck className="h-7 w-7 text-[#0B5CAB]" />
            </div>
            <DialogTitle className="text-xl" style={{ fontFamily: 'Space Grotesk' }}>
              Confirm {type}
            </DialogTitle>
            <p className="text-sm text-muted-foreground mt-1">Please review the details below</p>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Amount */}
          <div className="text-center">
            <p className="text-3xl font-bold tabular-nums" style={{ fontFamily: 'Space Grotesk' }}>
              {formatCurrency(amount)}
            </p>
            {convertedAmount && targetCurrency && (
              <p className="text-sm text-muted-foreground mt-1">
                ≈ {formatCurrency(convertedAmount)} {targetCurrency} at rate {fxRate}
              </p>
            )}
          </div>

          {/* Transfer Details */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            {fromAccount && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">From</span>
                <span className="font-medium">{fromAccount}</span>
              </div>
            )}
            {recipient && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">To</span>
                <span className="font-medium">{recipient}</span>
              </div>
            )}
            {extraFields.map((field, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span className="text-muted-foreground">{field.label}</span>
                <span className="font-medium">{field.value}</span>
              </div>
            ))}
            <div className="flex justify-between text-sm font-semibold pt-2 border-t">
              <span>Total Amount</span>
              <span className="tabular-nums">{formatCurrency(amount)}</span>
            </div>
          </div>

          {/* Warning */}
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
            <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">
              By confirming, you authorize this transaction. Funds may be held 3–5 business days for FDIC insurance verification.
            </p>
          </div>
        </div>

        <DialogFooter className="flex gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onClose(false)} className="flex-1" data-testid="pre-confirm-cancel">
            Cancel
          </Button>
          <Button onClick={onConfirm} className="flex-1" disabled={loading} data-testid="pre-confirm-submit">
            {loading ? 'Processing...' : (<>Confirm & Send <ArrowRight className="h-4 w-4 ml-1" /></>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
